"use client";

import { useMutation as useApolloMutation } from "@apollo/client/react";
import { useMutation } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import type { SerializedViewState } from "@/components/shared/UniversalCRMTable/types/shared";
import type { SavedView, SavedViewVisibility } from "@/lib/types/saved-views";
import {
  CreateSavedViewDocument,
  UpdateSavedViewDocument,
  DeleteSavedViewDocument,
  ToggleSavedViewFavoriteDocument,
  DeleteSavedViewFavoriteDocument,
  type Saved_ViewsInsertInput,
  type Saved_ViewsUpdateInput,
} from "@/src/gql/graphql";
import { serializeGraphqlInput } from "@/lib/graphql/serialize-input";
import { isFeatureEnabled, isFavoritesEnabled, pickFiltersForStorage } from "./saved-views-utils";

/**
 * The saved-view READS (`useSavedViews`, `useSavedViewFavorites`) run through
 * Apollo, not React Query — so a `queryClient.invalidateQueries(["saved-views",
 * resource])` here matched NOTHING and the dropdown kept showing the old list
 * until a full page reload. (`["saved-views", resource]` is the SERVER-side
 * Aurora cache key in `lib/cache/server-cache-wrapper.ts`; it was never a
 * client query key.) Refetch the Apollo operations BY NAME instead: Apollo
 * re-runs every active query with that name using its own variables, so each
 * mounted list refreshes for its own `resource` without this file having to
 * know which resources are on screen.
 */
const SAVED_VIEW_LIST_OPERATIONS = ["GetSavedViews"] as const;
const SAVED_VIEW_FAVORITE_OPERATIONS = [
  "GetSavedViewFavorites",
  "GetAllSavedViewFavorites",
] as const;

export function useCreateSavedView() {
  const enabled = isFeatureEnabled();
  const { appUser } = useUniversalAuth();
  const [createMutation] = useApolloMutation(CreateSavedViewDocument, {
    refetchQueries: [...SAVED_VIEW_LIST_OPERATIONS],
  });

  return useMutation({
    mutationKey: ["saved-views", "create"],
    mutationFn: async (payload: {
      resource: string;
      name: string;
      filters: SerializedViewState;
      visibility?: SavedViewVisibility;
      shared_with_user_ids?: string[];
    }) => {
      if (!enabled) throw new Error("Saved views feature is disabled");
      if (!appUser?.id) throw new Error("User not authenticated");

      // `filters` is the pg_graphql JSON scalar: codegen types it as an object but
      // the wire format is a JSON string, so it goes through serializeGraphqlInput
      // (main-11) rather than being hand-stringified and cast.
      const input: Saved_ViewsInsertInput = serializeGraphqlInput("saved_views", {
        resource: payload.resource,
        name: payload.name,
        filters: pickFiltersForStorage(payload.filters ?? {}),
        visibility: payload.visibility ?? "private",
        shared_with_user_ids: payload.shared_with_user_ids ?? [],
        created_by: appUser.id,
      });

      const result = await createMutation({ variables: { input } });

      if (result.error) {
        throw new Error(result.error?.message || "Failed to create view");
      }

      const record = result.data?.insertIntosaved_viewsCollection?.records?.[0];
      if (!record) throw new Error("No record returned from mutation");

      return {
        id: record.id,
        resource: record.resource,
        name: record.name,
        filters: record.filters || {},
        visibility: record.visibility,
        shared_with_user_ids: record.shared_with_user_ids || [],
        created_by: record.created_by,
        created_at: record.created_at,
        updated_at: record.updated_at,
      } as SavedView;
    },
    onSuccess: async () => {
      toast.success("View saved");
    },
    onError: (err: Error) => toast.error(err?.message || "Failed to save view"),
  });
}

export function useUpdateSavedView() {
  const enabled = isFeatureEnabled();
  const [updateMutation] = useApolloMutation(UpdateSavedViewDocument, {
    refetchQueries: [...SAVED_VIEW_LIST_OPERATIONS],
  });

  return useMutation({
    mutationKey: ["saved-views", "update"],
    mutationFn: async (payload: {
      id: string;
      resource: string;
      updates: Partial<Pick<SavedView, "name" | "filters" | "visibility" | "shared_with_user_ids">>;
    }) => {
      if (!enabled) throw new Error("Saved views feature is disabled");

      const input: Saved_ViewsUpdateInput = serializeGraphqlInput("saved_views", {
        ...payload.updates,
        filters: payload.updates.filters
          ? pickFiltersForStorage(payload.updates.filters)
          : undefined,
      });

      const result = await updateMutation({ variables: { id: payload.id, input } });

      if (result.error) {
        throw new Error(result.error?.message || "Failed to update view");
      }

      const record = result.data?.updatesaved_viewsCollection?.records?.[0];
      if (!record) throw new Error("No record returned from mutation");

      return {
        id: record.id,
        resource: record.resource,
        name: record.name,
        filters: record.filters || {},
        visibility: record.visibility,
        shared_with_user_ids: record.shared_with_user_ids || [],
        created_by: record.created_by,
        created_at: record.created_at,
        updated_at: record.updated_at,
      } as SavedView;
    },
    onSuccess: async () => {
      toast.success("View updated");
    },
    onError: (err: Error) => toast.error(err?.message || "Failed to update view"),
  });
}

export function useDeleteSavedView() {
  const enabled = isFeatureEnabled();
  const [deleteMutation] = useApolloMutation(DeleteSavedViewDocument, {
    refetchQueries: [...SAVED_VIEW_LIST_OPERATIONS],
  });

  return useMutation({
    mutationKey: ["saved-views", "delete"],
    mutationFn: async (payload: { id: string; resource: string }) => {
      if (!enabled) throw new Error("Saved views feature is disabled");

      const result = await deleteMutation({
        variables: { id: payload.id },
      });

      if (result.error) {
        throw new Error(result.error?.message || "Failed to delete view");
      }

      return { id: payload.id, resource: payload.resource };
    },
    onSuccess: async () => {
      toast.success("View deleted");
    },
    onError: (err: Error) => toast.error(err?.message || "Failed to delete view"),
  });
}

export function useToggleSavedViewFavorite(resource: string) {
  const enabled = isFavoritesEnabled();
  const { appUser } = useUniversalAuth();
  const [addFavoriteMutation] = useApolloMutation(ToggleSavedViewFavoriteDocument, {
    refetchQueries: [...SAVED_VIEW_FAVORITE_OPERATIONS],
  });
  const [removeFavoriteMutation] = useApolloMutation(DeleteSavedViewFavoriteDocument, {
    refetchQueries: [...SAVED_VIEW_FAVORITE_OPERATIONS],
  });

  return useMutation({
    // Scoped by resource so two mounted lists (contacts + deals) do not share
    // one mutation identity.
    mutationKey: ["saved-view-favorites", "toggle", resource],
    mutationFn: async (payload: { viewId: string; favorite: boolean }) => {
      if (!enabled) throw new Error("Favorites feature is disabled");
      if (!appUser?.id) throw new Error("User not authenticated");

      if (payload.favorite) {
        const result = await addFavoriteMutation({
          variables: { userId: appUser.id, viewId: payload.viewId },
        });
        if (result.error) {
          throw new Error(result.error?.message || "Failed to add favorite");
        }
      } else {
        const result = await removeFavoriteMutation({
          variables: { userId: appUser.id, viewId: payload.viewId },
        });
        if (result.error) {
          throw new Error(result.error?.message || "Failed to remove favorite");
        }
      }

      return { viewId: payload.viewId, favorite: payload.favorite };
    },
    onSuccess: () => {
      toast.success("Favorites updated");
    },
    onError: (err: Error) => toast.error(err?.message || "Failed to update favorites"),
  });
}
